import { FC, useState } from 'react';
import { Box, Dialog, makeStyles } from '@material-ui/core';
import { SBase, SButton, SText } from '@setvi/shared/components';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import KeyboardArrowDownRoundedIcon from '@material-ui/icons/KeyboardArrowDownRounded';
import moment, { Moment } from 'moment';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { renewQuoteV2Mutation } from 'Services/Mutations/QuotesV2';
import { QuotesV2QueryKey } from 'Services/Query/QuotesV2';
import type { PriceRefreshMethod } from 'Services/Query/QuotesV2/Types';
import { enqueueSnackbar } from 'notistack';
import { ExtendDateDialog } from './extend-date-dialog';
import { useQuoteCache } from '../../hooks/use-quote-cache';
import { ButtonContainer, IconWrapper, TitleContainer } from './styled';

export { ExtendDateDialog } from './extend-date-dialog';

const getUpdatedDate = (daysToAdd: number): string =>
  moment().add(daysToAdd, 'days').format('MM/DD/YYYY');

const useStyles = makeStyles(() => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
    padding: '0px 24px 24px'
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  dateRow: {
    display: 'flex',
    gap: 4
  },
  footer: {
    marginTop: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px 24px'
  }
}));

interface ExpiredQuoteDialogProps {
  quoteId: string;
  validUntil?: number;
  enableValidityDate?: boolean;
  onClose: () => void;
}

export const ExpiredQuoteDialog: FC<ExpiredQuoteDialogProps> = ({
  quoteId,
  validUntil,
  enableValidityDate,
  onClose
}) => {
  const classes = useStyles();
  const [showDateDialog, setShowDateDialog] = useState(false);
  const queryClient = useQueryClient();
  const cache = useQuoteCache(quoteId);
  const { mutationFn } = renewQuoteV2Mutation();

  const { mutateAsync: renewQuote, isLoading } = useMutation({
    mutationFn: (params: {
      validUntil: string;
      refreshMethod: PriceRefreshMethod;
    }) => mutationFn({ quoteId, ...params }),
    onSuccess: async () => {
      await cache.refetchQuote();
      queryClient.invalidateQueries({
        queryKey: [QuotesV2QueryKey.GET_QUOTES]
      });
    }
  });

  const handleChooseDate = () => {
    setShowDateDialog(true);
  };

  const handleConfirmDate = async (date: Moment) => {
    await renewQuote({
      refreshMethod: 'KEEP_PRICING',
      validUntil: date.format('YYYY-MM-DD')
    });

    enqueueSnackbar('Quote extended successfully', {
      variant: 'success'
    });

    setShowDateDialog(false);
    onClose();
  };

  const onExtend = async () => {
    await renewQuote({
      refreshMethod: 'KEEP_PRICING',
      validUntil: moment()
        .add(validUntil + 1, 'days')
        .format('YYYY-MM-DD')
    });

    enqueueSnackbar('Quote extended successfully', {
      variant: 'success'
    });

    onClose();
  };

  return (
    <SBase
      width={600}
      disablePadding
      style={{
        background: '#F8F9FA'
      }}
      header={{
        title: (
          <TitleContainer>
            <IconWrapper>
              <ErrorOutlineIcon htmlColor="#fff" />
            </IconWrapper>
            <SText
              title="Quote Expired - Action Required"
              fontColor="#20262D"
              weight="semibold"
              size="xl"
            />
          </TitleContainer>
        ),
        onClose
      }}>
      <Box className={classes.content}>
        <SText fontColor="#333D47" noLineClamp>
          Quote expiration date has passed. Please see the updated date below
          based on company quoting policy.
        </SText>
        {!enableValidityDate && validUntil && (
          <Box className={classes.dateRow}>
            <SText size="md">The updated quote date is</SText>
            <SText size="md" weight="bold">
              {getUpdatedDate(validUntil)}
            </SText>
          </Box>
        )}
      </Box>
      <Box className={classes.footer}>
        <SButton
          color="default"
          variant="text"
          onClick={onClose}
          style={{
            borderRadius: 36,
            color: '#5D7083'
          }}>
          Cancel
        </SButton>

        <ButtonContainer>
          <SButton
            variant={enableValidityDate ? 'outlined' : 'contained'}
            onClick={onExtend}
            disabled={isLoading}
            loading={isLoading}
            style={{
              borderRadius: 36
            }}>
            Extend for {validUntil} Days
          </SButton>
          {enableValidityDate && (
            <SButton
              disabled={isLoading}
              onClick={handleChooseDate}
              endIcon={
                <KeyboardArrowDownRoundedIcon
                  style={{
                    marginBottom: 2
                  }}
                />
              }
              style={{
                borderRadius: 36
              }}>
              Choose Date
            </SButton>
          )}
        </ButtonContainer>
      </Box>

      <Dialog
        PaperProps={{ style: { borderRadius: 20 } }}
        open={showDateDialog}
        onClose={() => setShowDateDialog(false)}>
        <ExtendDateDialog
          defaultDays={validUntil}
          isLoading={isLoading}
          onConfirm={handleConfirmDate}
          onClose={() => setShowDateDialog(false)}
        />
      </Dialog>
    </SBase>
  );
};
